import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, FileDown, Plus, Sparkles, Upload, FileText, X, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function PatientDetail() {
  const { id } = useParams();
  const [patient, setPatient] = useState(null);
  const [cases, setCases] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [appts, setAppts] = useState([]);

  const refresh = useCallback(async () => {
    const [p, c, rx, a] = await Promise.all([
      api.get(`/patients/${id}`),
      api.get(`/cases`, { params: { patient_id: id }}),
      api.get(`/prescriptions`, { params: { patient_id: id }}),
      api.get(`/appointments`, { params: { patient_id: id }}),
    ]);
    setPatient(p.data); setCases(c.data); setPrescriptions(rx.data); setAppts(a.data);
  }, [id]);

  useEffect(() => {
    refresh().catch((err) => {
      console.error("Failed to load patient:", err);
      toast.error("Failed to load patient");
    });
  }, [refresh]);

  if (!patient) return <p className="text-[color:var(--muted)]">Loading…</p>;

  return (
    <div data-testid="patient-detail-page">
      <Link to="/patients" className="inline-flex items-center gap-2 text-xs font-medium text-[color:var(--muted)] hover:text-[color:var(--ink)] mb-5" data-testid="back-to-patients">
        <ArrowLeft className="w-4 h-4" /> All patients
      </Link>

      <div className="bg-white rounded-2xl border border-[color:var(--border)] p-6 md:p-8 mb-6">
        <div className="flex flex-wrap gap-6 items-start">
          <span className="w-16 h-16 rounded-2xl bg-[color:var(--primary-light)] grid place-items-center text-[color:var(--primary-hover)] text-2xl font-heading">{patient.name?.[0]}</span>
          <div className="flex-1 min-w-0">
            <h1 className="font-heading text-3xl font-medium text-[color:var(--ink)]">{patient.name}</h1>
            <p className="text-sm text-[color:var(--muted)] mt-1">{patient.gender || "—"} · {patient.phone || "—"} · {patient.email || "—"}</p>
            <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
              <Info label="Date of birth" value={patient.dob || "—"} />
              <Info label="Occupation" value={patient.occupation || "—"} />
              <Info label="Allergies" value={patient.allergies || "—"} />
              <Info label="Tags" value={(patient.chronic_tags || []).join(", ") || "—"} />
            </div>
          </div>
        </div>
      </div>

      <Tabs defaultValue="cases" className="">
        <TabsList className="bg-white border border-[color:var(--border)] rounded-full p-1" data-testid="patient-tabs">
          <TabsTrigger value="cases" className="rounded-full" data-testid="tab-cases">Case-taking</TabsTrigger>
          <TabsTrigger value="rx" className="rounded-full" data-testid="tab-rx">Prescriptions</TabsTrigger>
          <TabsTrigger value="appts" className="rounded-full" data-testid="tab-appts">Appointments</TabsTrigger>
        </TabsList>

        <TabsContent value="cases" className="mt-5">
          <CasesPanel patientId={id} cases={cases} refresh={refresh} />
        </TabsContent>
        <TabsContent value="rx" className="mt-5">
          <RxPanel patientId={id} prescriptions={prescriptions} refresh={refresh} />
        </TabsContent>
        <TabsContent value="appts" className="mt-5">
          <ApptsPanel appts={appts} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-[10px] font-semibold tracking-[0.18em] uppercase text-[color:var(--muted)]">{label}</p>
      <p className="text-[color:var(--ink)] mt-0.5">{value}</p>
    </div>
  );
}

function CasesPanel({ patientId, cases, refresh }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [open, setOpen] = useState(false);
  const emptyForm = {
    summary: "",
    mental_symptoms: "", physical_generals: "", thermal_state: "", sleep: "",
    food_cravings: "", emotional_patterns: "", past_history: "",
    family_history: "", miasmatic_tendencies: "", notes: "",
  };
  const [form, setForm] = useState(emptyForm);
  const [attachments, setAttachments] = useState([]); // [{file_id, filename}]
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiText, setAiText] = useState(""); const [aiResult, setAiResult] = useState("");
  const [aiLoad, setAiLoad] = useState(false);

  const resetDialog = () => {
    setForm(emptyForm);
    setAttachments([]);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Only PDF files supported");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/cases/upload-pdf", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachments((prev) => [...prev, { file_id: data.file_id, filename: data.filename }]);
      const extracted = (data.extracted_text || "").trim();
      if (extracted) {
        setForm((prev) => ({
          ...prev,
          summary: prev.summary
            ? `${prev.summary}\n\n--- From ${data.filename} ---\n${extracted}`
            : extracted,
        }));
        toast.success(`Extracted ${data.page_count} page(s) into Case Summary`);
      } else {
        toast.warning("PDF uploaded but no extractable text was found (scanned image?)");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeAttachment = (file_id) => {
    setAttachments((prev) => prev.filter((a) => a.file_id !== file_id));
  };

  const save = async () => {
    setLoading(true);
    try {
      await api.post("/cases", { patient_id: patientId, ...form, attachments });
      toast.success("Case saved");
      setOpen(false);
      resetDialog();
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const aiSummary = async (text) => {
    setAiLoad(true); setAiResult("");
    try {
      const { data } = await api.post("/ai/case-summary", { text });
      setAiResult(data.summary);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAiLoad(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-heading text-lg font-medium">Case history ({cases.length})</h3>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetDialog(); }}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white" data-testid="add-case-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New case
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="case-dialog">
            <DialogHeader><DialogTitle className="font-heading">Homoeopathic case-taking</DialogTitle></DialogHeader>

            {/* Case Summary + PDF upload */}
            <div className="bg-[color:var(--bg-marketing)] border border-[color:var(--border)] rounded-xl p-4 mt-3 mb-1">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">Case summary</label>
                <div className="flex items-center gap-2">
                  <input
                    ref={fileRef}
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={handleUpload}
                    className="hidden"
                    data-testid="case-pdf-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="rounded-full text-xs h-8"
                    data-testid="case-upload-pdf-btn"
                  >
                    <Upload className="w-3.5 h-3.5 mr-1" /> {uploading ? "Extracting…" : "Upload PDF"}
                  </Button>
                </div>
              </div>
              <Textarea
                rows={5}
                value={form.summary}
                onChange={(e) => setForm({ ...form, summary: e.target.value })}
                placeholder="Type a case summary or upload a PDF to auto-extract text here…"
                className="bg-white"
                data-testid="case-summary"
              />
              {attachments.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {attachments.map((a) => (
                    <span key={a.file_id} className="inline-flex items-center gap-1.5 bg-white border border-[color:var(--border)] rounded-full pl-3 pr-1.5 py-1 text-xs" data-testid={`case-attachment-${a.file_id}`}>
                      <FileText className="w-3.5 h-3.5 text-[color:var(--secondary)]" />
                      <span className="text-[color:var(--ink)] max-w-[180px] truncate">{a.filename}</span>
                      <button
                        type="button"
                        onClick={() => removeAttachment(a.file_id)}
                        className="ml-1 w-5 h-5 grid place-items-center rounded-full hover:bg-[color:var(--border)]"
                        aria-label="Remove attachment"
                        data-testid={`case-attachment-remove-${a.file_id}`}
                      >
                        <X className="w-3 h-3 text-[color:var(--muted)]" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-4 mt-3">
              {[
                ["mental_symptoms","Mental symptoms"],
                ["physical_generals","Physical generals"],
                ["thermal_state","Thermal state"],
                ["sleep","Sleep"],
                ["food_cravings","Food cravings/aversions"],
                ["emotional_patterns","Emotional patterns"],
                ["past_history","Past history"],
                ["family_history","Family history"],
                ["miasmatic_tendencies","Miasmatic tendencies"],
              ].map(([k,l]) => (
                <div key={k}>
                  <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">{l}</label>
                  <Textarea rows={2} value={form[k]} onChange={e => setForm({...form, [k]: e.target.value})} className="mt-1.5" data-testid={`case-${k}`} />
                </div>
              ))}
              <div className="md:col-span-2">
                <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">Additional notes</label>
                <Textarea rows={3} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="mt-1.5" data-testid="case-notes" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} data-testid="case-cancel">Cancel</Button>
              <Button onClick={save} disabled={loading} className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white" data-testid="case-save">{loading ? "Saving…" : "Save case"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {cases.length === 0 ? (
        <p className="bg-white border border-[color:var(--border)] rounded-2xl py-14 text-center text-sm text-[color:var(--muted)]">No cases recorded yet.</p>
      ) : (
        <ul className="space-y-3">
          {cases.map((c) => (
            <li key={c.id} className="bg-white border border-[color:var(--border)] rounded-2xl p-5" data-testid={`case-item-${c.id}`}>
              <div className="flex justify-between items-start mb-2">
                <p className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">{new Date(c.created_at).toLocaleString()}</p>
                <div className="flex items-center gap-2">
                  <Dialog open={aiOpen} onOpenChange={(v) => { setAiOpen(v); if (v) { const text = Object.entries(c).filter(([k,val]) => typeof val === "string" && !["id","created_at","patient_id","created_by"].includes(k) && val).map(([k,val]) => `${k}: ${val}`).join("\n"); setAiText(text); setAiResult(""); }}}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" className="text-xs rounded-full" data-testid={`ai-summarise-${c.id}`}>
                        <Sparkles className="w-3.5 h-3.5 mr-1" /> AI summary
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader><DialogTitle className="font-heading">AI case summary</DialogTitle></DialogHeader>
                      <Textarea value={aiText} onChange={e => setAiText(e.target.value)} rows={6} />
                      <Button onClick={() => aiSummary(aiText)} disabled={aiLoad} className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white">{aiLoad ? "Generating…" : "Generate summary"}</Button>
                      {aiResult && <div className="bg-[color:var(--bg-marketing)] border border-[color:var(--border)] rounded-xl p-4 text-sm whitespace-pre-wrap">{aiResult}</div>}
                    </DialogContent>
                  </Dialog>
                  {isAdmin && (
                    <button
                      onClick={async () => {
                        if (!window.confirm("Delete this case record?")) return;
                        try {
                          await api.delete(`/cases/${c.id}`);
                          toast.success("Case deleted");
                          refresh();
                        } catch (err) {
                          toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
                        }
                      }}
                      className="text-[color:var(--muted)] hover:text-[color:var(--error)] p-1"
                      aria-label="Delete case"
                      data-testid={`case-delete-${c.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              {c.summary && (
                <div className="mb-3 p-3 bg-[color:var(--bg-marketing)] border border-[color:var(--border)] rounded-xl">
                  <p className="text-[10px] font-semibold tracking-[0.18em] uppercase text-[color:var(--muted)] mb-1">Case summary</p>
                  <p className="text-sm text-[color:var(--ink)] whitespace-pre-wrap leading-relaxed">{c.summary}</p>
                </div>
              )}
              <div className="grid md:grid-cols-2 gap-3 text-sm">
                {Object.entries({
                  "Mental": c.mental_symptoms, "Physical": c.physical_generals, "Thermal": c.thermal_state,
                  "Sleep": c.sleep, "Cravings": c.food_cravings, "Emotional": c.emotional_patterns,
                  "Past": c.past_history, "Family": c.family_history, "Miasms": c.miasmatic_tendencies,
                }).filter(([,v]) => v).map(([k,v]) => (
                  <p key={k}><strong className="text-[color:var(--muted)] text-xs uppercase tracking-wider">{k}:</strong> <span className="text-[color:var(--ink)]">{v}</span></p>
                ))}
              </div>
              {c.notes && <p className="mt-3 text-sm text-[color:var(--ink)]"><strong className="text-xs uppercase tracking-wider text-[color:var(--muted)]">Notes:</strong> {c.notes}</p>}
              {Array.isArray(c.attachments) && c.attachments.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {c.attachments.map((a) => (
                    <span
                      key={a.file_id}
                      className="inline-flex items-center gap-1.5 bg-[color:var(--bg-marketing)] border border-[color:var(--border)] rounded-full pl-3 pr-1.5 py-1 text-xs"
                      data-testid={`case-file-${a.file_id}`}
                    >
                      <FileText className="w-3.5 h-3.5 text-[color:var(--secondary)]" />
                      <a
                        href="#"
                        onClick={async (e) => {
                          e.preventDefault();
                          try {
                            const resp = await api.get(`/cases/files/${a.file_id}`, { responseType: "blob" });
                            window.open(URL.createObjectURL(resp.data), "_blank");
                          } catch (err) {
                            toast.error("File unavailable");
                          }
                        }}
                        className="text-[color:var(--ink)] hover:underline max-w-[200px] truncate"
                      >
                        {a.filename}
                      </a>
                      {isAdmin && (
                        <button
                          type="button"
                          onClick={async () => {
                            if (!window.confirm(`Delete attachment "${a.filename}"?`)) return;
                            try {
                              await api.delete(`/cases/files/${a.file_id}`);
                              toast.success("Attachment deleted");
                              refresh();
                            } catch (err) {
                              toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
                            }
                          }}
                          className="ml-1 w-5 h-5 grid place-items-center rounded-full hover:bg-[color:var(--border)]"
                          aria-label="Delete attachment"
                          data-testid={`case-file-delete-${a.file_id}`}
                        >
                          <X className="w-3 h-3 text-[color:var(--muted)]" />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RxPanel({ patientId, prescriptions, refresh }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ remedy: "", potency: "30C", dosage: "3 pellets, twice daily", instructions: "", follow_up_date: "", notes: "" });
  const [loading, setLoading] = useState(false);

  const save = async () => {
    setLoading(true);
    try {
      await api.post("/prescriptions", { patient_id: patientId, ...form });
      toast.success("Prescription saved");
      setOpen(false);
      setForm({ remedy: "", potency: "30C", dosage: "3 pellets, twice daily", instructions: "", follow_up_date: "", notes: "" });
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-heading text-lg font-medium">Prescriptions ({prescriptions.length})</h3>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white" data-testid="add-rx-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New prescription
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl" data-testid="rx-dialog">
            <DialogHeader><DialogTitle className="font-heading">New prescription</DialogTitle></DialogHeader>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Remedy *" value={form.remedy} onChange={v => setForm({...form, remedy: v})} testid="rx-remedy" />
              <Field label="Potency *" value={form.potency} onChange={v => setForm({...form, potency: v})} testid="rx-potency" />
              <Field label="Dosage *" value={form.dosage} onChange={v => setForm({...form, dosage: v})} testid="rx-dosage" />
              <Field label="Follow-up date" type="date" value={form.follow_up_date} onChange={v => setForm({...form, follow_up_date: v})} testid="rx-followup" />
              <div className="md:col-span-2">
                <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">Instructions</label>
                <Textarea rows={2} value={form.instructions} onChange={e => setForm({...form, instructions: e.target.value})} className="mt-1.5" data-testid="rx-instructions" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">Notes</label>
                <Textarea rows={2} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="mt-1.5" data-testid="rx-notes-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} data-testid="rx-cancel">Cancel</Button>
              <Button onClick={save} disabled={loading || !form.remedy} className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white" data-testid="rx-save">{loading ? "Saving…" : "Save prescription"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {prescriptions.length === 0 ? (
        <p className="bg-white border border-[color:var(--border)] rounded-2xl py-14 text-center text-sm text-[color:var(--muted)]">No prescriptions yet.</p>
      ) : (
        <ul className="space-y-3">
          {prescriptions.map((rx) => (
            <li key={rx.id} className="bg-white border border-[color:var(--border)] rounded-2xl p-5" data-testid={`rx-item-${rx.id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-heading text-xl text-[color:var(--ink)]">{rx.remedy} <span className="text-[color:var(--primary)]">{rx.potency}</span></p>
                  <p className="text-sm text-[color:var(--muted)] mt-1">{rx.dosage}</p>
                  {rx.follow_up_date && <p className="text-xs text-[color:var(--muted)] mt-2">Follow-up: {rx.follow_up_date}</p>}
                </div>
                <a href="#" onClick={async (e) => { e.preventDefault(); const r = await api.get(`/prescriptions/${rx.id}/pdf`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); }} data-testid={`rx-pdf-${rx.id}`}>
                  <Button variant="outline" size="sm" className="text-xs rounded-full"><FileDown className="w-3.5 h-3.5 mr-1" /> PDF</Button>
                </a>
              </div>
              {rx.instructions && <p className="mt-3 text-sm text-[color:var(--ink)]">{rx.instructions}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ApptsPanel({ appts }) {
  if (appts.length === 0)
    return <p className="bg-white border border-[color:var(--border)] rounded-2xl py-14 text-center text-sm text-[color:var(--muted)]">No appointments.</p>;
  return (
    <ul className="space-y-3">
      {appts.map((a) => (
        <li key={a.id} className="bg-white border border-[color:var(--border)] rounded-2xl p-5" data-testid={`apt-item-${a.id}`}>
          <p className="font-medium text-[color:var(--ink)]">{new Date(a.scheduled_at).toLocaleString()}</p>
          <p className="text-sm text-[color:var(--muted)]">{a.reason || "—"} · <span className="capitalize">{a.mode?.replace("_", " ")}</span> · <span className="capitalize">{a.status}</span></p>
        </li>
      ))}
    </ul>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">{label}</label>
      <Input type={type} value={value} onChange={e => onChange(e.target.value)} className="mt-1.5" data-testid={testid} />
    </div>
  );
}
